/**
 * Demo Route Guard
 * Manages route navigation during demo, prevents breaking the demo flow
 * Handles route transitions, state preservation, and fallback logic
 */

import {
  ROUTE_GUARD_CONFIG,
  ROUTE_NAVIGATION_CONFIG,
  ROUTE_TRANSITION_CONFIG,
  STATE_PRESERVATION_CONFIG,
  ROUTE_FALLBACK_CONFIG,
  ROUTE_DETECTION_CONFIG,
  ROUTE_NAVIGATION_ERRORS,
} from '@/demo/constants';
import { getDemoSynchronizationService } from './demoSynchronizationService';

/**
 * Demo Route Guard Service
 * Singleton for managing route guards during demo session
 */
class DemoRouteGuard {
  constructor() {
    /**
     * Is demo session active
     * @type {boolean}
     */
    this.isDemoActive = false;

    /**
     * Current demo step route
     * @type {string}
     */
    this.currentStepRoute = null;

    /**
     * Allowed routes during demo
     * @type {Set<string>}
     */
    this.allowedRoutes = new Set();

    /**
     * Blocked routes
     * @type {Set<string>}
     */
    this.blockedRoutes = new Set(ROUTE_FALLBACK_CONFIG.BLOCKED_ROUTES);

    /**
     * Safe fallback routes
     * @type {Set<string>}
     */
    this.safeRoutes = new Set(ROUTE_FALLBACK_CONFIG.SAFE_FALLBACK_ROUTES);

    /**
     * Last valid route
     * @type {string}
     */
    this.lastValidRoute = null;

    /**
     * Route transition history
     * @type {Array<{from: string, to: string, timestamp: number, success: boolean}>}
     */
    this.transitionHistory = [];

    /**
     * Demo state backup
     * @type {Object}
     */
    this.stateBackup = null;

    /**
     * Router instance
     * @type {Object}
     */
    this.router = null;

    /**
     * Synchronization service reference
     * @type {DemoSynchronizationService}
     */
    this.syncService = null;
  }

  /**
   * Initialize route guard for demo session
   * @param {Object} router - Vue Router instance
   * @param {Array<Object>} demoSteps - Demo steps configuration
   * @returns {boolean} - True if initialization successful
   */
  initializeGuard(router, demoSteps) {
    try {
      this.router = router;
      this.syncService = getDemoSynchronizationService();
      this.isDemoActive = true;

      // Build allowed routes from demo steps
      this._buildAllowedRoutes(demoSteps);

      // Setup route guards
      if (ROUTE_GUARD_CONFIG.ENABLE_GUARDS) {
        this._setupRouteGuards();
      }

      if (ROUTE_GUARD_CONFIG.LOG_GUARDS) {
        console.log('[DemoRouteGuard] Guard initialized with', this.allowedRoutes.size, 'allowed routes');
      }

      return true;
    } catch (error) {
      console.error('[DemoRouteGuard] Failed to initialize:', error);
      this.isDemoActive = false;
      return false;
    }
  }

  /**
   * Build set of allowed routes from demo steps
   * @private
   * @param {Array<Object>} demoSteps - Demo steps
   */
  _buildAllowedRoutes(demoSteps) {
    this.allowedRoutes.clear();

    // Add demo step routes
    demoSteps.forEach((step) => {
      if (step.pageRoute) {
        this.allowedRoutes.add(step.pageRoute);
      }
    });

    // Add configured allowed routes
    ROUTE_GUARD_CONFIG.ALLOWED_ROUTES.forEach((route) => {
      this.allowedRoutes.add(route);
    });

    if (ROUTE_GUARD_CONFIG.LOG_GUARDS) {
      console.log('[DemoRouteGuard] Allowed routes:', Array.from(this.allowedRoutes));
    }
  }

  /**
   * Setup Vue Router guards for demo
   * @private
   */
  _setupRouteGuards() {
    if (!this.router) {
      return;
    }

    // Setup beforeEach guard
    if (ROUTE_GUARD_CONFIG.GUARD_TYPE === 'before-nav' || ROUTE_GUARD_CONFIG.GUARD_TYPE === 'both') {
      this.router.beforeEach(async (to, from, next) => {
        return this._handleBeforeNavigation(to, from, next);
      });
    }

    // Setup afterEach guard for logging
    this.router.afterEach((to, from) => {
      this._handleAfterNavigation(to, from);
    });
  }

  /**
   * Handle navigation attempt
   * @private
   * @param {Object} to - Target route
   * @param {Object} from - Source route
   * @param {Function} next - Vue Router next function
   * @returns {Promise<void>}
   */
  async _handleBeforeNavigation(to, from, next) {
    if (!this.isDemoActive) {
      next();
      return;
    }

    // Check if navigation is blocked
    if (this._isNavigationBlocked(to, from)) {
      if (ROUTE_GUARD_CONFIG.SHOW_CONFIRMATION) {
        // Emit event for confirmation dialog
        if (this.syncService && this.syncService.eventBus) {
          this.syncService.eventBus.emit('demo:navigation-blocked', {
            to: to.path,
            from: from.path,
            message: ROUTE_GUARD_CONFIG.CONFIRMATION_MESSAGE,
          });
        }
      }

      next(false);
      return;
    }

    // Preserve state before navigation
    if (STATE_PRESERVATION_CONFIG.PRESERVE_ON_ROUTE_CHANGE) {
      this._preserveState();
    }

    // Update current step route
    if (this.allowedRoutes.has(to.path)) {
      this.currentStepRoute = to.path;
      this.lastValidRoute = from.path;
    }

    next();
  }

  /**
   * Handle after navigation
   * @private
   * @param {Object} to - Target route
   * @param {Object} from - Source route
   */
  _handleAfterNavigation(to, from) {
    // Record transition
    this.transitionHistory.push({
      from: from.path,
      to: to.path,
      timestamp: Date.now(),
      success: true,
    });

    // Trim history if too long
    if (this.transitionHistory.length > ROUTE_TRANSITION_CONFIG.MAX_TRANSITION_HISTORY) {
      this.transitionHistory.shift();
    }

    if (ROUTE_GUARD_CONFIG.LOG_GUARDS) {
      console.log('[DemoRouteGuard] Navigation:', from.path, '->', to.path);
    }
  }

  /**
   * Check if navigation should be blocked
   * @private
   * @param {Object} to - Target route
   * @param {Object} from - Source route
   * @returns {boolean} - True if navigation should be blocked
   */
  _isNavigationBlocked(to, from) {
    // Allow same route navigation
    if (ROUTE_GUARD_CONFIG.ALLOW_SAME_ROUTE_NAV && to.path === from.path) {
      return false;
    }

    // Check if target is blocked route
    if (this.blockedRoutes.has(to.path)) {
      return true;
    }

    // Strict validation: only allow step routes
    if (ROUTE_GUARD_CONFIG.STRICT_ROUTE_VALIDATION) {
      return !this.allowedRoutes.has(to.path);
    }

    return false;
  }

  /**
   * Preserve demo state to storage
   * @private
   */
  _preserveState() {
    try {
      if (!STATE_PRESERVATION_CONFIG.ENABLE_STATE_PRESERVATION) {
        return;
      }

      if (!this.syncService) {
        return;
      }

      const state = {
        currentStepIndex: this.syncService.currentStepIndex,
        currentRoute: this.currentStepRoute,
        timestamp: Date.now(),
        version: STATE_PRESERVATION_CONFIG.STATE_VERSION,
      };

      if (STATE_PRESERVATION_CONFIG.USE_LOCAL_STORAGE) {
        localStorage.setItem(STATE_PRESERVATION_CONFIG.STATE_STORAGE_KEY, JSON.stringify(state));
      }

      if (STATE_PRESERVATION_CONFIG.USE_SESSION_STORAGE) {
        sessionStorage.setItem(STATE_PRESERVATION_CONFIG.SESSION_STATE_KEY, JSON.stringify(state));
      }

      if (STATE_PRESERVATION_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoRouteGuard] State preserved:', state);
      }
    } catch (error) {
      console.warn('[DemoRouteGuard] Failed to preserve state:', error);
    }
  }

  /**
   * Restore demo state from storage
   * @returns {Object|null} - Restored state or null
   */
  restoreState() {
    try {
      if (!STATE_PRESERVATION_CONFIG.ENABLE_STATE_PRESERVATION) {
        return null;
      }

      let state = null;

      // Try session storage first
      if (STATE_PRESERVATION_CONFIG.USE_SESSION_STORAGE) {
        const sessionState = sessionStorage.getItem(STATE_PRESERVATION_CONFIG.SESSION_STATE_KEY);
        if (sessionState) {
          state = JSON.parse(sessionState);
        }
      }

      // Fall back to local storage
      if (!state && STATE_PRESERVATION_CONFIG.USE_LOCAL_STORAGE) {
        const localState = localStorage.getItem(STATE_PRESERVATION_CONFIG.STATE_STORAGE_KEY);
        if (localState) {
          state = JSON.parse(localState);
        }
      }

      if (state && STATE_PRESERVATION_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoRouteGuard] State restored:', state);
      }

      return state;
    } catch (error) {
      console.warn('[DemoRouteGuard] Failed to restore state:', error);
      return null;
    }
  }

  /**
   * Handle route navigation error and attempt recovery
   * @param {string} errorCode - Error code from ROUTE_NAVIGATION_ERRORS
   * @param {Object} context - Error context
   * @returns {Promise<boolean>} - True if recovery successful
   */
  async handleNavigationError(errorCode, context = {}) {
    try {
      const error = ROUTE_NAVIGATION_ERRORS[errorCode];

      if (!error) {
        console.warn(`[DemoRouteGuard] Unknown error code: ${errorCode}`);
        return false;
      }

      if (ROUTE_FALLBACK_CONFIG.LOG_FALLBACKS) {
        console.log(`[DemoRouteGuard] Navigation error: ${error.message}`);
      }

      if (!error.recoverable) {
        return false;
      }

      // Execute fallback action
      return await this._executeFallbackAction(error.fallbackAction, context);
    } catch (error) {
      console.error('[DemoRouteGuard] Error handling navigation error:', error);
      return false;
    }
  }

  /**
   * Execute fallback action for route navigation error
   * @private
   * @param {string} action - Fallback action type
   * @param {Object} context - Error context
   * @returns {Promise<boolean>} - True if fallback successful
   */
  async _executeFallbackAction(action, context = {}) {
    try {
      switch (action) {
        case 'retry':
          return await this._retryNavigation(context);

        case 'return-home':
          return await this._navigateToRoute(ROUTE_FALLBACK_CONFIG.FALLBACK_HOME_ROUTE);

        case 'return-previous':
          return await this._navigateToRoute(this.lastValidRoute);

        case 'skip-step':
          return await this._skipCurrentStep();

        case 'show-confirmation':
          // Confirmation is handled by component
          return false;

        default:
          return false;
      }
    } catch (error) {
      console.error('[DemoRouteGuard] Error executing fallback action:', error);
      return false;
    }
  }

  /**
   * Retry navigation to current route
   * @private
   * @param {Object} context - Retry context
   * @returns {Promise<boolean>} - True if retry successful
   */
  async _retryNavigation(context = {}) {
    const maxRetries = ROUTE_FALLBACK_CONFIG.MAX_FALLBACK_ATTEMPTS;
    let attempts = 0;

    while (attempts < maxRetries) {
      try {
        await new Promise((resolve) => setTimeout(resolve, ROUTE_FALLBACK_CONFIG.FALLBACK_RETRY_DELAY));

        if (this.syncService && this.currentStepRoute) {
          await this.syncService._navigateToStepRoute({
            pageRoute: this.currentStepRoute,
          });
          return true;
        }
      } catch (error) {
        attempts++;
        if (attempts >= maxRetries) {
          return false;
        }
      }
    }

    return false;
  }

  /**
   * Navigate to specific route
   * @private
   * @param {string} route - Target route path
   * @returns {Promise<boolean>} - True if navigation successful
   */
  async _navigateToRoute(route) {
    try {
      if (!this.router || !route) {
        return false;
      }

      if (ROUTE_NAVIGATION_CONFIG.NAVIGATION_METHOD === 'router-push') {
        if (ROUTE_NAVIGATION_CONFIG.REPLACE_HISTORY) {
          await this.router.replace(route);
        } else {
          await this.router.push(route);
        }
      } else {
        window.location.href = route;
      }

      return true;
    } catch (error) {
      console.error('[DemoRouteGuard] Failed to navigate to route:', error);
      return false;
    }
  }

  /**
   * Skip current step and advance to next
   * @private
   * @returns {Promise<boolean>} - True if skip successful
   */
  async _skipCurrentStep() {
    try {
      if (!this.syncService) {
        return false;
      }

      return await this.syncService.advanceToNextStep();
    } catch (error) {
      console.error('[DemoRouteGuard] Failed to skip step:', error);
      return false;
    }
  }

  /**
   * Stop the route guard (end demo session)
   */
  stopGuard() {
    this.isDemoActive = false;
    this.currentStepRoute = null;
    this.allowedRoutes.clear();
    this.transitionHistory = [];
    this.stateBackup = null;

    if (ROUTE_GUARD_CONFIG.LOG_GUARDS) {
      console.log('[DemoRouteGuard] Guard stopped');
    }
  }

  /**
   * Get route guard status
   * @returns {Object} - Guard status information
   */
  getStatus() {
    return {
      isDemoActive: this.isDemoActive,
      currentStepRoute: this.currentStepRoute,
      lastValidRoute: this.lastValidRoute,
      allowedRoutesCount: this.allowedRoutes.size,
      transitionHistoryLength: this.transitionHistory.length,
    };
  }
}

/**
 * Global singleton instance
 * @type {DemoRouteGuard|null}
 */
let demoRouteGuardInstance = null;

/**
 * Get or create the global DemoRouteGuard instance
 * @returns {DemoRouteGuard}
 */
export function getDemoRouteGuard() {
  if (!demoRouteGuardInstance) {
    demoRouteGuardInstance = new DemoRouteGuard();
  }
  return demoRouteGuardInstance;
}

/**
 * Reset the route guard (mainly for testing)
 */
export function resetDemoRouteGuard() {
  if (demoRouteGuardInstance) {
    demoRouteGuardInstance.stopGuard();
  }
  demoRouteGuardInstance = null;
}

export default DemoRouteGuard;
